import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { presets } from "config/images";
import ImageComponent from "theme/components/atoms/Image/ImageComponent";
import { useShop } from "web/core/shop/ShopContext";
import makeImagesProps from "theme/components/atoms/Image/makeImagesProps";
import useImageStatus from "theme/components/atoms/Image/useImageStatus";

/**
 * @typedef {object} Props
 * @property {string} src
 * @property {string} alt
 * @property {string} format
 * @property {"default" | "full" | "rounded" | "full-rounded" | "cover"} appearance
 * @property {string} [bg]
 * @property {boolean} [cover]
 * @property {boolean} [dangerouslyDisableLazyLoad]
 * @property {string} [placeholderSrc]
 * @property {string} [errorSrc]
 * @property {string} [className]
 * @property {string} [sizes]
 * @property {object} [focalPoint]
 */

/** @type {React.FunctionComponent<Props>} */
const Image = ({
  src,
  alt,
  format,
  bg,
  cover,
  appearance,
  dangerouslyDisableLazyLoad,
  placeholderSrc,
  errorSrc,
  className,
  sizes,
  focalPoint,
  ...extraImgProps
}) => {
  const imgProps = {
    decoding: dangerouslyDisableLazyLoad ? "sync" : "async",
    ...extraImgProps,
  };

  const shop = useShop();
  const images = useMemo(() => {
    return makeImagesProps(
      (shop && shop.imageBaseUrl) || "",
      src,
      format,
      bg,
      cover,
      appearance.indexOf("full") > -1,
      placeholderSrc,
      errorSrc,
      focalPoint
    );
  }, [
    shop,
    src,
    format,
    bg,
    cover,
    appearance,
    placeholderSrc,
    errorSrc,
    focalPoint,
  ]);

  const { imageRef, status, size } = useImageStatus(
    images,
    dangerouslyDisableLazyLoad,
    presets[format]
  );

  const ratio =
    size && size.width && size.height ? size.height / size.width : undefined;

  return (
    <ImageComponent
      ref={imageRef}
      imgProps={imgProps}
      cover={cover}
      appearance={appearance}
      images={images}
      ratio={ratio}
      sizes={sizes}
      alt={alt}
      status={status}
      dangerouslyDisableLazyLoad={dangerouslyDisableLazyLoad}
      preload={false}
    />
  );
};

Image.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  cover: PropTypes.bool,
  format: PropTypes.oneOf(Object.keys(presets)),
  appearance: PropTypes.oneOf([
    "default",
    "full",
    "rounded",
    "full-rounded",
    "cover",
  ]),
  bg: PropTypes.string,
  dangerouslyDisableLazyLoad: PropTypes.bool,
  focalPoint: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
  }),
};

Image.defaultProps = {
  appearance: "default",
};

export default Image;
